import {
  AnnouncementInterface,
  CustomerResponseInterface,
  MemberInterface,
  MemberResponseInterface,
  OrganisationResponseInterface,
  CheckInInterface,
  OrganisationFeedbackInterface
} from '@careapp/angular-sdk';
import { ORGANISATION_ADMIN_ROLE } from '@careapp/core';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { createSelector } from 'reselect';

import * as fromAnnouncements from './announcements.reducer';
import * as fromAuth from './auth.reducer';
import * as fromBulk from './bulk-request.reducer';
import * as fromCheckIns from './check-ins.reducer';
import * as fromCustomerTeam from './customer-team.reducer';
import * as fromCustomers from './customers.reducer';
import { getSelectedId, ORGANISATIONS } from './data-list.reducer';
import { DataListState } from './data-list.state';
import * as fromFeedback from './feedback.reducer';
import * as fromFileUpload from './file-upload.reducer';
import * as fromInvite from './invite.reducer';
import * as fromLocation from './location.reducer';
import * as fromMemoCategories from './memo-categories.reducer';
import * as fromOrganisations from './organisations.reducer';
import * as fromStaff from './staff.reducer';
import * as fromStatistics from './statistics.reducer';
import * as fromStorageSync from './storage-sync.reducer';
import { logoutReducer } from './logout.reducer';

/**
 * The compose function is one of our most handy tools. In basic terms, you give
 * it any number of functions and it returns a function. This new function
 * takes a value and chains it through every composed function, returning
 * the output.
 *
 * More: https://drboolean.gitbooks.io/mostly-adequate-guide/content/ch5.html
 */
/**
 * storeFreeze prevents state from being mutated. When mutation occurs, an
 * exception will be thrown. This is useful during development mode to
 * ensure that none of the reducers accidentally mutates the state.
 */
export * from './data-list.reducer';
/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
  announcements: DataListState<AnnouncementInterface>;
  auth: fromAuth.State;
  bulkAction: fromBulk.State;
  checkIns: DataListState<CheckInInterface>;
  customers: DataListState<CustomerResponseInterface>;
  customerTeam: DataListState<MemberResponseInterface>;
  feedback: DataListState<OrganisationFeedbackInterface>;
  fileUpload: fromFileUpload.State;
  invite: fromInvite.State;
  location: fromLocation.State;
  memoCategories: fromMemoCategories.State;
  organisations: DataListState<OrganisationResponseInterface>;
  router: RouterReducerState;
  staff: DataListState<MemberInterface>;
  storageSync: fromStorageSync.StorageSyncState;
  statistics: fromStatistics.State;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const REDUCERS = {
  announcements: fromAnnouncements.reducer,
  auth: fromAuth.reducer,
  bulkAction: fromBulk.reducer,
  checkIns: fromCheckIns.reducer,
  customers: fromCustomers.reducer,
  customerTeam: fromCustomerTeam.reducer,
  feedback: fromFeedback.reducer,
  fileUpload: fromFileUpload.reducer,
  invite: fromInvite.reducer,
  location: fromLocation.reducer,
  memoCategories: fromMemoCategories.reducer,
  organisations: fromOrganisations.reducer,
  router: routerReducer,
  staff: fromStaff.reducer,
  statistics: fromStatistics.reducer
};

export const META_REDUCERS = [fromStorageSync.reducer, logoutReducer];

/**
 * A selector function is a map function factory. We pass it parameters and it
 * returns a function that maps from the larger state tree into a smaller
 * piece of state. This selector simply selects the `books` state.
 *
 * Selectors are used with the `select` operator.
 *
 * ```ts
 * class MyComponent {
 * 	constructor(state$: Observable<State>) {
 * 	  this.booksState$ = state$.select(getBooksState);
 * 	}
 * }
 * ```
 */
export const getAuthenticationState = (state: State) => state.auth;
export const getBulkActionState = (state: State) => state.bulkAction;
export const getCheckInsState = (state: State) => state.checkIns;
export const getCustomersState = (state: State) => state.customers;
export const getCustomerTeamState = (state: State) => state.customerTeam;
export const getFileUploadState = (state: State) => state.fileUpload;
export const getInviteState = (state: State) => state.invite;
export const getLocationState = (state: State) => state.location;
export const getMemoCategoriesState = (state: State) => state.memoCategories;
export const getOrganisationState = (state: State) => state.organisations;
export const getRouterState = (state: State) => state.router;
export const getStaffState = (state: State) => state.staff;
export const getStatisticsState = (state: State) => state.statistics;
export const getStorageSyncState = (state: State) => state.storageSync;

/**
 * Every reducer module has selector functions but have no knowledge of overall state tree.
 * To make them useable we need to inject in their state segment so createSelector can be called
 */
export const auth = fromAuth.createSelectors(getAuthenticationState);
export const bulk = fromBulk.createSelectors(getBulkActionState);
export const invite = fromInvite.createSelectors(getInviteState);
export const memoCategories = fromMemoCategories.createSelectors(getMemoCategoriesState);
export const org = fromOrganisations.createSelectors(getOrganisationState);
export const statistics = fromStatistics.createSelectors(getStatisticsState);

export const getFileUploadError = (id: string) =>
  createSelector(
    getFileUploadState,
    fromFileUpload.fileError(id)
  );
export const getFileUploadProgress = (id: string) =>
  createSelector(
    getFileUploadState,
    fromFileUpload.fileProgress(id)
  );
export const getFileUploadSuccess = (id: string) =>
  createSelector(
    getFileUploadState,
    fromFileUpload.fileSuccess(id)
  );
export const getFileUploadResponse = (id: string) =>
  createSelector(
    getFileUploadState,
    fromFileUpload.fileResponse(id)
  );

export const isOrgAdmin = createSelector(
  auth.isAdmin,
  org.currentOrgRoles,
  (rootAdmin, roles) => {
    return rootAdmin || roles.includes(ORGANISATION_ADMIN_ROLE);
  }
);

export const getLocation = createSelector(
  getLocationState,
  fromLocation.getLocation
);
export const isLocationLoading = createSelector(
  getLocationState,
  fromLocation.isLoading
);

export const getRehydrated = createSelector(
  getStorageSyncState,
  fromStorageSync.getRehydrated
);

export const getCurrentOrganisationCategories = createSelector(
  getSelectedId(ORGANISATIONS),
  memoCategories.getOrganisationCategories,
  (selectedId: string, orgCategories) =>
    orgCategories[selectedId]
      ? orgCategories[selectedId].sort((a, b) => a.settings.ordinal - b.settings.ordinal)
      : []
);

export const isAppAuthorized = auth.isReady;

export const isAppReady = createSelector(
  getStorageSyncState,
  fromStorageSync.isReady
);

/**
 * The selector that Storage Sync uses to decide what to write and when
 */
export const storageSyncSelector = createSelector(
  auth.getPersisted,
  authState => {
    return { auth: authState };
  }
);
